import {
  Incubation,
  Measurement,
  Procedure,
} from "@/services/backend/procedures/procedure/procedure";
import { Username } from "@/shared/components/domain/username/username";
import { growthMedia } from "@/shared/components/enum-labels";
import { Card } from "@/shared/components/ui/card";
import { formatDate, formatTime } from "@/shared/utilities/date";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { de } from "date-fns/locale";
import { ReactElement } from "react";
import { GrowthMedium } from "@/services/backend/types";
import EvaluableBadge from "../samples/evaluable-badge";

const getGrowthMediaLabel = (incubation: Incubation): string => {
  if (incubation.sequenceNumber === 1) {
    if (incubation.growthMedium === GrowthMedium.Caso) {
      return "Bakterien";
    }
  }

  if (incubation.sequenceNumber === 2) {
    if (incubation.growthMedium === GrowthMedium.Caso) {
      return "Schimmelpilze";
    }
    return "Schimmelpilze / Hefen";
  }

  return "";
};

function getCountingLabel(measurement: Measurement): string {
  if (measurement.measurement != null) {
    return measurement.measurement.toString();
  }
  if (measurement.exceedsThreshold) {
    return "> 100";
  }

  return "Kein Wert vorhanden";
}

interface IncubationMetadataRowProps {
  procedure: Procedure;
}

function IncubationMetadataRow({
  procedure,
}: IncubationMetadataRowProps): ReactElement {
  if (
    !procedure ||
    !procedure.incubation ||
    procedure.incubation.length === 0
  ) {
    return <div>Keine Inkubationen gefunden</div>;
  }

  return (
    <div className="flex flex-col space-y-4">
      {procedure?.incubation.map((inc) => (
        <Card key={inc.id} className="flex flex-col space-y-2 p-4 shadow-none">
          <div className="flex flex-col space-y-2">
            <div className="flex flex-row space-x-2">
              <p className="font-bold">Art: </p>
              <p>
                {growthMedia[inc.growthMedium]} ({getGrowthMediaLabel(inc)})
              </p>
            </div>
            <EvaluableBadge evaluableType={inc.evaluableType} />
          </div>
          {inc.measurements.length > 0 && (
            <Card className="shadow-none">
              <Table>
                <MeasurementHistoryTableHeader />
                <MeasurementHistoryTableBody measurements={inc.measurements} />
              </Table>
            </Card>
          )}
        </Card>
      ))}
    </div>
  );
}

function MeasurementHistoryTableHeader() {
  return (
    <TableHeader className="border-b border-gray-200">
      <TableRow>
        <TableHead>Zählung:</TableHead>
        <TableHead>Gezählt von:</TableHead>
        <TableHead>Zähldatum:</TableHead>
        <TableHead>Uhrzeit:</TableHead>
      </TableRow>
    </TableHeader>
  );
}

function MeasurementHistoryTableBody({
  measurements,
}: {
  measurements: Measurement[];
}) {
  return (
    <TableBody>
      {measurements.map((measure) => (
        <TableRow key={measure.id}>
          <TableCell>{getCountingLabel(measure)}</TableCell>
          <TableCell>
            <Username userId={measure.employeeId} />
          </TableCell>
          <TableCell>{formatDate(measure.measurementDate, de)}</TableCell>
          <TableCell>{formatTime(measure.measurementDate, de)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
}

export default IncubationMetadataRow;
